import { Car, KeyRound } from 'lucide-preact';
import { route } from 'preact-router';
import { useEffect, useRef, useState } from 'preact/hooks';
import { downloadReceipt } from '../../booking/downloadReceipt.js';
import { PushOptInBanner } from '../../components/PushOptInBanner.js';
import { StarRating } from '../../components/StarRating.js';
import { CarPositionMap } from '../../components/booking/CarPositionMap.js';
import { DrivingAreaCard, type PickupZone } from '../../components/booking/DrivingAreaCard.js';
import { EndBookingAction } from '../../components/booking/EndBookingAction.js';
import { type LateQrBlock, LateQrPayment } from '../../components/booking/LateQrPayment.js';
import { type MileageUse, MileageUseCard } from '../../components/booking/MileageUseCard.js';
import { PaymentPauseNotice } from '../../components/booking/PaymentPauseNotice.js';
import { DuitNowQrPanel, type QrData } from '../../components/payment/DuitNowQrPanel.js';
import { type AlternativeCar, AlternativeCars } from '../../components/ui/AlternativeCars.js';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { type CarFuel, FuelGauge } from '../../components/ui/FuelGauge.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { goneMessage } from '../../components/ui/resultWording.js';
import { useAuth } from '../../context/AuthContext.js';
import { useFetch } from '../../hooks/useFetch.js';
import { usePaymentPause } from '../../hooks/usePaymentPause.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';
import { useWindowRefresh } from '../../hooks/useWindowRefresh.js';
import { ApiError, api } from '../../services/api.js';
import { getExtendContext } from '../../services/extendContext.js';
import type { ChannelOption } from '../../services/payments.js';
import { mytDateTime } from '../../utils/mytTime.js';
import { CarConditionPhotosCard, type PhotoWindows } from './CarConditionPhotosCard.js';
import { reasonCopy } from './extendReasons.js';
import { TRIP_PURPOSE_LABELS, type Trip } from './tripDraft.js';

/**
 * 130 (FR-002/FR-007): the ONLY status a customer may cancel from — nothing has been collected
 * yet, so there is no refund to process. An allowlist, not a blocklist: the previous
 * `!TERMINAL_STATUSES` test is how this control silently spread to five statuses as the enum grew,
 * including three where the customer had already paid. A status added later is not cancellable
 * until someone adds it here deliberately. The server enforces the same rule — see
 * bookingService.cancel — so removing this control is the courtesy, not the barrier.
 */
const CUSTOMER_CANCELLABLE_STATUSES = ['awaiting_payment'];

/**
 * 206 (#285, research.md R2): the two moments the push opt-in soft-ask is shown — right after a
 * booking is confirmed (US1) and again while it is active (US2, `stages.ts`'s own
 * `ACTIVE_STATUSES`, duplicated here since `app/` cannot import `backend/`). A future change to
 * either definition must update this list too.
 */
const PUSH_OPT_IN_STATUSES = ['confirmed', 'checked_out', 'checked_in'];

/** 195 (#13/#16): refuses anything but a real http(s) link before it can become an `<a href>`. */
function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol) ? value : null;
  } catch {
    return null;
  }
}

interface MyReview {
  id: string;
  rating: number;
  comment?: string;
  car?: { id: string; name: string } | null;
}

/** 052: resolved vehicle summary. Null when the car record is gone (FR-005). */
interface BookingCar {
  id: string;
  /** Composed brand + model — never the `·` specification dump. */
  label: string;
  plate_number: string;
  image_url: string | null;
  /** 195 (#13/#16): present only while the backend judges this booking eligible
   *  (bookings/serializer.ts, bookings/tracking.ts) — absent, never re-derived here. */
  tracking_url?: string | null;
  /** 405 (#315): true while the platform lets this customer read the car's Cartrack position (the same window as the
   *  link, `bookings/tracking.ts`); absent otherwise. Never re-derived here. */
  live_position?: boolean;
  /** 408 (#1434): the car's fuel, present only while the platform shows it (confirmed or checked out, a car on
   *  Cartrack with a reading). Never re-derived here. */
  fuel?: CarFuel;
  /** 339 (#939): the car's lockbox code, present only inside the window the backend judges
   *  (bookings/lockbox.ts) — never re-derived here. */
  lockbox_code?: string | null;
  /** 339: before that window, for a paid booking, the instant the code will appear. */
  lockbox_code_from?: string | null;
  /** 387 (#1308): why the code is held back for the customer — the pickup photos, or a staff release after they were
   *  missed. Decided by the platform (`bookings/serializer.ts`), only worded here. */
  lockbox_code_requires?: 'pickup_photos' | 'staff_release' | null;
}

interface BookingLocation {
  id: string;
  name: string;
  city_name: string | null;
}

interface BookingSummary {
  /** 361: the customer's own trip answers, when the booking has them. */
  trip?: Trip;
  /** 053: canonical identifier. `_id` is still on the wire but deprecated. */
  id: string;
  car_id: string;
  pickup_at: string;
  return_at: string;
  total_price_minor: number;
  discount_minor: number;
  status: string;
  currency: string;
  car: BookingCar | null;
  location: BookingLocation | null;
  /** 200 (#291): already published to the booking's owner (bookings/serializer.ts) — this screen
   *  just never looked. Lets the screen tell a zero-due booking (voucher/personal-discount/gift-
   *  cert coverage) apart from one that genuinely needs payment. */
  pricing_snapshot?: { totals?: { amount_due_minor?: number; gift_cert_credit_minor?: number } };
  /** 304: set when this booking extends another (so it has no handover of its own). */
  parent_booking_id?: string;
  /** 337: false when staff switched customer inspection photos off; absent (an older backend) reads as on. */
  inspection_photos_enabled?: boolean;
  /** 421: extra time CARii added after the return, paid by CARii's QR; null when the rental has none. */
  late_payment?: LateQrBlock | null;
  /** 374: when the customer may send each photo set; absent while photos are off or from an older backend. */
  photo_windows?: PhotoWindows;
  /** 423 (#1546): the platform's shots per moment for this car (owner reads only). */
  photo_angles?: { checkout?: string[]; checkin?: string[] };
  /** 325 (#477): the rental this booking belongs to, whichever leg this page was opened on. */
  rental?: RentalSummary;
  /** 390 (#1310): the platform's word that the rental's receipt can be downloaded (finished rentals only). */
  receipt_available?: boolean;
  /** 407 (#1370): the platform's word that the customer may end the rental now (`endRental`'s rule). */
  can_end?: boolean;
  /** 414: the return photos are in and a payment holds the end back. */
  end_waits_on_payment?: boolean;
  /** 416 (#1431): the car is outside the pickup area right now, with the km so far. */
  zone_status?: { outside: boolean; km_outside: number; location_name: string } | null;
  /** 416: the pickup location's driving area, for the customer's own reference (paid rentals). */
  pickup_zone?: PickupZone | null;
  /** 407: the rental is still checked out after its return time. */
  return_overdue?: boolean;
  /** 417 (#1464): the customer's mileage package and, during the rental, the km used. */
  mileage_use?: MileageUse | null;
}

/** `summariseRental`'s shape (`backend/src/modules/bookings/rental.ts`), dates as sent. */
interface RentalSummary {
  /** The original booking's id: the rental's key. */
  rental_id?: string;
  legs: Array<{
    id: string;
    status: string;
    pickup_at: string;
    return_at: string;
    total_price_minor: number;
    is_extension: boolean;
  }>;
  pickup_at: string;
  return_at: string;
  total_price_minor: number;
  extension_count: number;
  status: string;
}

interface BookingConfirmProps {
  id?: string;
  path?: string;
  /** 363: `?review=1`, from the "How was your trip?" invitation — the review is outlined. */
  review?: string;
}

/**
 * 339: the lockbox code's opening time, in Malaysia wall-clock time like every rental time (#541),
 * whatever zone the customer's device is set to.
 */
function rentalDateTime(iso: string): string {
  return mytDateTime(iso);
}

export function BookingConfirmPage({ id, review }: BookingConfirmProps) {
  const goToSignIn = useSignInRedirect();
  // reloadKey lets a successful/rejected cancel force a refetch so the shown
  // status reflects server truth (useFetch re-runs when its path changes).
  const [reloadKey, setReloadKey] = useState(0);
  const bookingRead = useFetch<BookingSummary>(id ? `/bookings/${id}?r=${reloadKey}` : null);
  const { data: booking, status: bookingStatus } = bookingRead;
  // 353: re-read when the lockbox code and the location link open or close, and on return to the app.
  useWindowRefresh(booking, () => setReloadKey((k) => k + 1));
  const loading = bookingStatus === 'loading' || bookingStatus === 'idle';
  const { isImpersonating } = useAuth();
  const [paying, setPaying] = useState(false);
  // 390: the receipt download's own state, so a failure is said beside its button.
  const [receiptState, setReceiptState] = useState<'idle' | 'saving' | 'failed'>('idle');
  // 459: set only for a QR channel's response — renders DuitNowQrPanel inline in place of the
  // usual redirect. Cleared at the start of every fresh Pay Now / regenerate attempt.
  const [qrData, setQrData] = useState<QrData | null>(null);
  const [error, setError] = useState('');
  // 213 (#309): populated only when the refusal that set `error` carried alternatives — a car
  // refused for an unrelated reason (e.g. a provider outage) shows no list.
  const [alternatives, setAlternatives] = useState<AlternativeCar[]>([]);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState('');
  // 200 (#291): a voucher/personal-discount/gift-cert combination can cover the whole booking
  // already — nothing left to pay. Reused below both to auto-settle without a click and to hide
  // an otherwise-dead "Pay Now".
  const isZeroDue =
    booking?.status === 'awaiting_payment' &&
    booking?.pricing_snapshot?.totals?.amount_due_minor === 0;
  // 406 (#1369): the banks' Friday downtime; the server refuses a bank payment then, so the page says so instead.
  const { paused: paymentsPaused } = usePaymentPause();
  // Ref, not state: must not allow a second auto-settle call to slip in during the gap before
  // `paying` state actually commits from the first one.
  const autoSettledRef = useRef(false);

  // 459 (T013): every currently-enabled channel, fetched once the booking is actually awaiting
  // payment. `channels` stays `undefined` while this hasn't resolved to a real
  // `{channels: [...]}` shape yet (still loading, or — same effect — a caller's own useFetch stub
  // answering something else entirely) — that is deliberately treated the SAME as "not fetched",
  // never as "no channels available", so the plain single-channel Pay Now button below (today's
  // only behavior) never regresses while this call is in flight.
  const channelsPath =
    id && booking?.status === 'awaiting_payment' && !isZeroDue
      ? `/bookings/${id}/payment-channel-options`
      : null;
  const { data: channelsData } = useFetch<{ channels: ChannelOption[] }>(channelsPath);
  const channels = Array.isArray(channelsData?.channels) ? channelsData.channels : undefined;
  const noChannelsAvailable = channels !== undefined && channels.length === 0;
  const showChannelPicker = channels !== undefined && channels.length > 1;
  const [selectedChannel, setSelectedChannel] = useState<number | null>(null);
  // Intentionally re-runs only when the fetched channel LIST changes, not on every
  // `selectedChannel` change it reads — the latter would re-pick channel[0] the instant the
  // customer selects anything else.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see prose above
  useEffect(() => {
    if (
      channels &&
      channels.length > 0 &&
      (selectedChannel === null || !channels.some((c) => c.id === selectedChannel))
    ) {
      setSelectedChannel(channels[0]?.id ?? null);
    }
  }, [channels]);
  const selectedOption = channels?.find((c) => c.id === selectedChannel);
  // 358: credit drawn by this booking (from "Use my credit" or a credit code).
  const creditUsedMinor = booking?.pricing_snapshot?.totals?.gift_cert_credit_minor ?? 0;

  async function handleCancel() {
    if (!id) return;
    setConfirmCancel(false);
    setCancelling(true);
    setError('');
    try {
      await api.post(`/bookings/${id}/cancel`);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? 'This booking can no longer be cancelled.'
          : 'Could not cancel this booking. Please try again.',
      );
    } finally {
      setCancelling(false);
      setReloadKey((k) => k + 1);
    }
  }

  /**
   * 304 — replaces navigating straight to the old ExtendBookingPage. Asks
   * `GET /bookings/:id/extend-context` first: eligible means the normal booking-creation flow
   * gets a locked car_id/pickup_at/parent_booking_id to submit with, ineligible means the
   * customer never navigates at all and reads the refusal right here — cheaper than a round trip
   * through a page that would just refuse the same thing after a full load.
   */
  async function handleExtend() {
    if (!id) return;
    setExtending(true);
    setExtendError('');
    try {
      const eligibility = await getExtendContext(id);
      if (!eligibility.extendable || !eligibility.car_id || !eligibility.pickup_at) {
        setExtendError(
          reasonCopy(eligibility.reason ?? 'booking_not_extendable', eligibility.limits).message,
        );
        return;
      }
      const params = new URLSearchParams({
        pickup_at: eligibility.pickup_at,
        parent_booking_id: eligibility.tail_booking_id ?? id,
      });
      route(`/booking/${eligibility.car_id}?${params.toString()}`);
    } catch {
      setExtendError('Could not check whether this booking can be extended. Please try again.');
    } finally {
      setExtending(false);
    }
  }

  // 042: reviews are only writable on a completed booking. Look up the caller's
  // own review for this booking (0 or 1 items — R7 mechanism).
  const reviewsPath =
    booking?.status === 'completed' && id ? `/reviews?mine=1&booking_id=${id}` : null;
  const { data: reviewData } = useFetch<{ items: MyReview[] }>(reviewsPath);
  const existingReview = reviewData?.items?.[0] ?? null;
  // 363 (#1090, design 2g): a completed trip not yet rated leads with its review; once rated it sits below the
  // details. Neither renders until the lookup answers, so the form never flashes over an existing review.
  const reviewKnown = booking?.status === 'completed' && Boolean(id) && reviewData !== undefined;
  const reviewLeads = reviewKnown && !existingReview;

  // 200 (#291): a zero-due booking needs no click — settle it the moment the screen knows there's
  // nothing to pay, rather than showing a "Pay Now" the customer would otherwise have to press.
  useEffect(() => {
    if (isZeroDue && !autoSettledRef.current) {
      autoSettledRef.current = true;
      void handlePay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZeroDue]);

  async function saveReceipt() {
    if (!id || !booking) return;
    setReceiptState('saving');
    try {
      await downloadReceipt(id, booking.rental?.rental_id ?? id);
      setReceiptState('idle');
    } catch {
      setReceiptState('failed');
    }
  }

  async function handlePay() {
    if (!id) return;
    // 406: never ask for a bank payment during the pause (and never clear a QR already shown); a booking fully
    // covered by credit still settles.
    if (paymentsPaused && !isZeroDue) return;
    setPaying(true);
    setError('');
    setAlternatives([]);
    setQrData(null);
    try {
      // 459: send the chosen channel only once the picker has actually resolved one — omitting
      // the param defaults to channel 1 (FPX) server-side, matching today's behavior unchanged
      // for the common single-channel case.
      const url = selectedChannel
        ? `/bookings/${id}/payment-url?channel=${selectedChannel}`
        : `/bookings/${id}/payment-url`;
      const response = await api.get<{
        payment_url: string | null;
        status?: string;
        qr?: QrData;
      }>(url);
      // 459: a QR channel renders inline instead of redirecting — no `window.location.href` at
      // all. `paying` clears immediately since there is nothing further to wait ON here; the
      // panel's own poll loop takes over.
      if (response.qr) {
        setQrData(response.qr);
        setPaying(false);
        return;
      }
      // 🔴 200 (#291): a null payment_url on a successful response means the backend already
      // settled this booking (amount due was zero) — assigning it to window.location.href would
      // coerce to the literal string "null" and navigate nowhere. Reflect the settled state
      // instead, the same way handleCancel already forces a refetch after its own write.
      if (!response.payment_url) {
        setPaying(false);
        setReloadKey((k) => k + 1);
        return;
      }
      window.location.href = response.payment_url;
    } catch (err) {
      // 🔴 188: `||`, not `??`. A refusal carrying an EMPTY message set `error` to '', which is
      // falsy — so `{error && …}` rendered nothing, the button came back, and the customer was left
      // pressing Pay with no explanation anywhere. Found by 188's own test asserting the generic
      // fallback still appears for a failure that explains nothing; `??` only falls back on
      // null/undefined, and an empty string is neither.
      setError((err as { message?: string }).message || 'Failed to get payment URL');
      setAlternatives(err instanceof ApiError ? (err.alternatives as AlternativeCar[]) : []);
      setPaying(false);
    }
  }

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  // 064: this said "Booking not found" for every outcome — an ended session, an outage and a
  // deleted booking were indistinguishable, and only one of the three was true.
  if (bookingStatus !== 'ok' || !booking)
    return (
      <ResultState
        state={bookingRead}
        onRetry={bookingRead.retry}
        goneMessage={goneMessage('booking')}
        onwardLabel="My bookings"
        onOnward={() => route('/account/bookings')}
        onSignIn={goToSignIn}
      />
    );

  // 🔴 The backend now refines tracking_url to http(s) at write time, but this guard stays: a
  // pre-existing row or any future write path that skips validation must not turn into a real
  // `<a href="javascript:...">` on a customer-facing page.
  const trackingUrl = safeHttpUrl(booking.car?.tracking_url);
  // 387: the lockbox notice's link brings the photos card into view (it sits just above).
  const showPhotosCard = () => {
    const card = document.querySelector<HTMLElement>('[data-testid="car-condition-photos"]');
    card?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // 325: shown only for a rental of more than one leg; an ordinary booking's page is unchanged.
  const rental = booking.rental && booking.rental.legs.length > 1 ? booking.rental : undefined;
  // 421: the extra-time leg itself is paid by QR and a receipt, never through the gateway, and is not cancellable.
  const lateQrLeg = booking.late_payment?.booking_id === (booking.id ?? id);

  return (
    <div className="bg-bg px-4 py-6">
      {/* 208 (#308): had no back control at all before this feature. */}
      {/* 325: "Confirm Booking" only while payment is due; a booked or running rental is a booking. */}
      <PageHeader
        title={booking.status === 'awaiting_payment' ? 'Confirm Booking' : 'Booking'}
        fallback="/account/bookings"
      />
      {/* 325: the RENTAL's state, so an extension paid before it starts never reads "in use". */}
      <StatusBadge status={rental?.status ?? booking.status} />

      {PUSH_OPT_IN_STATUSES.includes(booking.status) && <PushOptInBanner />}

      {booking.late_payment && (
        <LateQrPayment
          latePayment={booking.late_payment}
          legs={booking.rental?.legs ?? []}
          paused={paymentsPaused}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}

      {reviewLeads && id && (
        <ReviewBlock bookingId={id} existing={null} lead highlight={review === '1'} />
      )}

      {/* 407 (#1370, mock-up screen 4): the rental stays active after its return time until it is ended. */}
      {booking.return_overdue && (
        <div
          data-testid="return-overdue"
          className="mt-4 rounded-xl bg-primary/15 px-4 py-3 text-sm font-semibold text-primary"
        >
          Your return time has passed.
          <span className="mt-0.5 block text-xs font-normal text-text">
            {/* 414: with customer photos on, the return photos are what end the booking. */}
            {booking.inspection_photos_enabled !== false
              ? 'Please return the car and send the return photos to end your booking.'
              : 'Please return the car and end your booking.'}{' '}
            Return was {mytDateTime(booking.rental?.return_at ?? booking.return_at)}.
          </span>
        </div>
      )}

      {/* 416 (#1431, mock-up screen 5): the car is outside its pickup area (the geo-fence's last reading). */}
      {booking.zone_status?.outside && (
        <div
          data-testid="zone-outside"
          className="mt-4 rounded-xl bg-warning/15 px-4 py-3 text-sm text-warning"
        >
          You are outside the {booking.zone_status.location_name} area ·{' '}
          {booking.zone_status.km_outside.toFixed(1)} km so far. Driving outside it may be charged
          per km.
        </div>
      )}

      {/* 417 (#1464, mock-up frame 4): the mileage package chosen at booking, with the km used. */}
      {booking.mileage_use && <MileageUseCard use={booking.mileage_use} />}

      {/* 052: which car this booking is for. A customer arriving from a payment link
          previously saw only dates and a total. */}
      <div className="mt-4 bg-surface-card rounded-xl p-4 flex items-center gap-3">
        {booking.car?.image_url ? (
          <img
            src={booking.car.image_url}
            alt={booking.car.label}
            className="w-20 h-14 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="w-20 h-14 rounded-lg bg-surface-mid shrink-0 flex items-center justify-center">
            <Car size={24} className="text-gray-500" aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0">
          {booking.car ? (
            <>
              <p className="font-semibold text-text truncate">{booking.car.label}</p>
              <p className="text-xs text-gray-500">{booking.car.plate_number}</p>
            </>
          ) : (
            <p className="font-semibold text-gray-400">Vehicle no longer listed</p>
          )}
        </div>
      </div>

      {/* 408 (mock-up screen 3): the car's fuel now, large, with when Cartrack read it. */}
      {booking.car?.fuel && (
        <div className="mt-3 bg-surface-card rounded-xl p-4">
          <FuelGauge fuel={booking.car.fuel} size="large" />
        </div>
      )}

      {/* 405 (#315, mock-up A1–A3): a car on Cartrack shows where it is; the pasted link is then only the map's
          fallback. A car not on Cartrack keeps the link exactly as before. */}
      {booking.car?.live_position && id ? (
        <CarPositionMap bookingId={id} fallbackUrl={trackingUrl} zone={booking.pickup_zone} />
      ) : (
        trackingUrl && (
          <a
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block text-center text-primary font-semibold underline"
          >
            {/* #1343: customers did not understand "Track your car"; this says what the link does. */}
            See where the car is
          </a>
        )
      )}

      {/* 416 (#1431, mock-up screen 6): with no live car map (before pickup, or a car not on Cartrack), the driving
          area on its own card. With a live map it is drawn there. */}
      {!booking.car?.live_position && booking.pickup_zone && (
        <DrivingAreaCard zone={booking.pickup_zone} when="rental" />
      )}

      {/* 374 (#1238): the pickup and return photos, each offered only inside the window the platform publishes (it
          replaces 210's status-tied buttons: 338's workflow closes a rental in one run, so `checked_in` never showed).
          337: absent while staff keep customer photos off. One card per rental, on every leg.
          387 (#1309): right after "See where the car is" and before the lockbox code, so a phone shows it without scrolling. */}
      {booking.inspection_photos_enabled !== false && booking.photo_windows && id && (
        <CarConditionPhotosCard
          rentalId={booking.rental?.rental_id ?? id}
          windows={booking.photo_windows}
          overdue={booking.return_overdue === true}
          endWaitsOnPayment={booking.end_waits_on_payment === true}
          angles={booking.photo_angles}
        />
      )}
      {/* 407: with customer photos switched off there is no photos card, so End booking stands on its own. */}
      {!(booking.inspection_photos_enabled !== false && booking.photo_windows) &&
        booking.can_end === true &&
        id && (
          <EndBookingAction
            bookingId={booking.rental?.rental_id ?? id}
            onEnded={() => setReloadKey((k) => k + 1)}
          />
        )}

      {(booking.car?.lockbox_code ||
        booking.car?.lockbox_code_from ||
        booking.car?.lockbox_code_requires) && (
        <div
          data-testid="lockbox-code"
          className="mt-3 bg-surface-card rounded-xl p-4 flex items-center gap-3"
        >
          <KeyRound size={22} className="text-primary shrink-0" aria-hidden="true" />
          {booking.car.lockbox_code ? (
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Lockbox code</p>
              <p className="font-mono text-2xl font-bold tracking-widest text-primary break-all">
                {booking.car.lockbox_code}
              </p>
            </div>
          ) : booking.car.lockbox_code_requires === 'staff_release' ? (
            // 387 (FR-007): the pickup photo time passed; only staff can release the code now.
            <p className="text-sm text-text">
              Your pickup photo time has passed. Contact support for your lockbox code.
            </p>
          ) : booking.car.lockbox_code_from ? (
            <p className="text-sm text-text">
              Your lockbox code appears at {rentalDateTime(booking.car.lockbox_code_from as string)}{' '}
              (Malaysia time),{' '}
              {booking.car.lockbox_code_requires === 'pickup_photos'
                ? 'once your pickup photos are sent.'
                : '15 minutes before pickup.'}
            </p>
          ) : (
            // 387 (FR-005): the code's time has come; it appears once the pickup photos are sent.
            <div className="min-w-0">
              <p className="text-sm text-text">Send your pickup photos to see your lockbox code.</p>
              <button
                type="button"
                onClick={showPhotosCard}
                className="mt-1 text-sm font-semibold text-primary underline"
              >
                Take pickup photos ↑
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 bg-surface-card rounded-xl p-4 flex flex-col gap-4">
        {booking.location && <Row label="Pickup location" value={booking.location.name} />}
        <Row label="Pickup" value={mytDateTime(booking.pickup_at)} />
        <Row label="Return" value={mytDateTime(booking.return_at)} />
        {/* 361 (FR-007): the customer reads their own trip answers. */}
        {booking.trip && (
          <div data-testid="booking-trip" className="flex flex-col gap-4">
            <Row
              label="Your trip"
              value={`${booking.trip.students} ${booking.trip.students === 1 ? 'student' : 'students'} · ${TRIP_PURPOSE_LABELS[booking.trip.purpose] ?? booking.trip.purpose}`}
            />
            <Row
              label="Food delivery"
              value={
                booking.trip.food_delivery
                  ? `OK${booking.trip.non_halal === undefined ? '' : booking.trip.non_halal ? ' · non-halal OK' : ' · halal only'}`
                  : 'Not OK'
              }
            />
          </div>
        )}
        {booking.discount_minor > 0 && (
          <Row label="Discount" value={`-RM ${(booking.discount_minor / 100).toFixed(2)}`} />
        )}
        <div className="border-t border-surface-mid pt-3 flex justify-between">
          <span className="text-text font-semibold">Total</span>
          <span className="text-primary font-bold text-xl" data-testid="booking-total">
            RM {((selectedOption?.total_minor ?? booking.total_price_minor) / 100).toFixed(2)}
          </span>
        </div>
        {/* 358 (FR-005): the credit this booking used, and what is still to pay after it. */}
        {creditUsedMinor > 0 && (
          <>
            <div
              data-testid="booking-credit"
              className="flex justify-between text-sm text-green-400"
            >
              <span>Credit used</span>
              <span>-RM {(creditUsedMinor / 100).toFixed(2)}</span>
            </div>
            <div data-testid="booking-due" className="flex justify-between">
              <span className="text-text font-semibold">Amount due</span>
              <span className="text-primary font-bold">
                RM{' '}
                {(
                  Math.max(
                    0,
                    (selectedOption?.total_minor ?? booking.total_price_minor) - creditUsedMinor,
                  ) / 100
                ).toFixed(2)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* 325 (#477): an extended rental is one rental. Its overall times and every leg (the original
          booking and each extension) with its own price and status; an unpaid leg opens to pay. */}
      {rental && (
        <div
          data-testid="rental-legs"
          className="mt-4 bg-surface-card rounded-xl p-4 flex flex-col gap-3"
        >
          <p className="text-text font-semibold">Your rental</p>
          <Row label="Starts" value={mytDateTime(rental.pickup_at)} />
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Ends</span>
            <span className="text-text" data-testid="rental-return">
              {mytDateTime(rental.return_at)}
            </span>
          </div>
          {rental.legs.map((leg, i) => (
            <div key={leg.id} className="border-t border-surface-mid pt-3 flex flex-col gap-1">
              <div className="flex justify-between items-center gap-2">
                <span className="text-sm font-semibold text-text">
                  {leg.is_extension ? `Extension ${i}` : 'Original booking'}
                </span>
                <span className="text-sm text-text whitespace-nowrap">
                  RM {(leg.total_price_minor / 100).toFixed(2)}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                {legTime(leg.pickup_at)} → {legTime(leg.return_at)}
              </span>
              <div className="flex justify-between items-center gap-2">
                <span className="whitespace-nowrap" data-testid="rental-leg-status">
                  <StatusBadge status={legStatus(leg)} />
                </span>
                {leg.status === 'awaiting_payment' && leg.id !== id && (
                  <Button
                    data-testid={`rental-leg-pay-${leg.id}`}
                    onClick={() => route(`/booking/${leg.id}/confirm`)}
                    variant="secondary"
                    size="sm"
                  >
                    Pay
                  </Button>
                )}
              </div>
            </div>
          ))}
          <div className="border-t border-surface-mid pt-3 flex justify-between">
            <span className="text-text font-semibold">Rental total</span>
            <span className="text-primary font-bold" data-testid="rental-total">
              RM {(rental.total_price_minor / 100).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4">
          <p className="text-sm text-red-400">{error}</p>
          <AlternativeCars items={alternatives} />
        </div>
      )}

      {booking.status === 'awaiting_payment' && !lateQrLeg ? (
        isImpersonating ? (
          <p className="mt-6 text-sm text-yellow-400" data-testid="impersonation-pay-blocked">
            Payment is disabled while impersonating this user.
          </p>
        ) : isZeroDue ? (
          // 200 (#291): nothing owed — the effect above settles this automatically, so there is
          // no action for the customer to take, only a moment to wait through.
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
            <Spinner size="sm" />
            Finalizing your booking…
          </div>
        ) : noChannelsAvailable ? (
          // 459 spec edge case: staff have enabled no channel at all — tell the customer plainly
          // rather than showing a Pay Now that can only fail.
          <p className="mt-6 text-sm text-gray-400" data-testid="no-payment-methods">
            No payment method is available right now. Please try again later.
          </p>
        ) : qrData && id ? (
          // 459: a QR channel renders inline in place of the picker/Pay Now — no redirect.
          <DuitNowQrPanel
            bookingId={id}
            qr={qrData}
            onPaid={() => setReloadKey((k) => k + 1)}
            onRegenerate={() => void handlePay()}
            paymentsPaused={paymentsPaused}
          />
        ) : paymentsPaused ? (
          <PaymentPauseNotice />
        ) : (
          <>
            {showChannelPicker && (
              <div
                className="mt-6 bg-surface-card rounded-xl p-3"
                role="radiogroup"
                aria-label="Payment method"
                data-testid="channel-picker"
              >
                {channels?.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center justify-between gap-2 py-2 cursor-pointer"
                    data-testid={`channel-option-${c.id}`}
                  >
                    <span className="flex items-center gap-2 text-sm text-text">
                      <input
                        type="radio"
                        name="payment-channel"
                        checked={selectedChannel === c.id}
                        onChange={() => setSelectedChannel(c.id)}
                      />
                      {c.name}
                    </span>
                    <span className="text-sm font-semibold text-text">
                      RM {(c.total_minor / 100).toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <Button onClick={handlePay} loading={paying} size="lg" className="w-full mt-6">
              Pay Now
            </Button>
          </>
        )
      ) : (
        <>
          {/* 390 (#1310): above "View My Bookings", only when the platform says the receipt is ready. */}
          {booking.receipt_available === true && (
            <>
              <Button
                onClick={saveReceipt}
                loading={receiptState === 'saving'}
                size="lg"
                className="w-full mt-6"
              >
                Download receipt
              </Button>
              {receiptState === 'failed' && (
                <p role="alert" className="mt-2 text-center text-sm text-red-400">
                  The receipt could not be downloaded. Please try again.
                </p>
              )}
            </>
          )}
          <Button
            onClick={() => route('/account/bookings')}
            variant="ghost"
            size="lg"
            className="w-full mt-6"
          >
            View My Bookings
          </Button>
        </>
      )}

      {['confirmed', 'checked_out'].includes(booking.status) && id && (
        <>
          <Button
            data-testid="extend-cta"
            onClick={handleExtend}
            loading={extending}
            variant="secondary"
            size="lg"
            className="w-full mt-3"
          >
            Extend Booking
          </Button>
          {extendError && (
            <p data-testid="extend-refusal" className="text-sm text-red-400 mt-2">
              {extendError}
            </p>
          )}
        </>
      )}

      {CUSTOMER_CANCELLABLE_STATUSES.includes(booking.status) && id && !lateQrLeg && (
        <Button
          data-testid="cancel-cta"
          onClick={() => setConfirmCancel(true)}
          loading={cancelling}
          variant="ghost"
          size="lg"
          className="w-full mt-3 text-red-400"
        >
          Cancel Booking
        </Button>
      )}

      <ConfirmDialog
        open={confirmCancel}
        danger
        title="Cancel this booking?"
        body="This releases your reserved vehicle. Any refund is handled separately by the operator."
        confirmLabel="Cancel booking"
        cancelLabel="Keep booking"
        onCancel={() => setConfirmCancel(false)}
        onConfirm={handleCancel}
      />

      {reviewKnown && !reviewLeads && id && (
        <ReviewBlock bookingId={id} existing={existingReview} />
      )}
    </div>
  );
}

function ReviewBlock({
  bookingId,
  existing,
  lead = false,
  highlight = false,
}: {
  bookingId: string;
  existing: MyReview | null;
  /** 363: the trip is done and unrated — the block leads the page, with large stars to tap. */
  lead?: boolean;
  /** 363: arrived from the invitation. */
  highlight?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(existing?.rating ?? 0);
  const [comment, setComment] = useState(existing?.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  // View mode: an existing review that is not currently being edited.
  if (existing && !editing && !done) {
    return (
      <div className="mt-8 bg-surface-card rounded-xl p-4" data-testid="review-view">
        <h2 className="text-lg font-semibold text-text mb-2">Your Review</h2>
        <StarRating value={existing.rating} />
        {existing.comment && <p className="mt-2 text-sm text-text">{existing.comment}</p>}
        <Button
          variant="ghost"
          className="mt-3"
          data-testid="review-edit"
          onClick={() => {
            setRating(existing.rating);
            setComment(existing.comment ?? '');
            setEditing(true);
          }}
        >
          Edit
        </Button>
      </div>
    );
  }

  async function submit() {
    if (rating < 1) {
      setErr('Please select a rating');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      if (existing) {
        await api.patch(`/reviews/${existing.id}`, { rating, comment });
      } else {
        await api.post('/reviews', { booking_id: bookingId, rating, comment });
      }
      setEditing(false);
      setDone(true);
    } catch (e) {
      setErr((e as { message?: string }).message ?? 'Failed to save review');
    } finally {
      setSaving(false);
    }
  }

  if (done) {
    return (
      <div className="mt-8 bg-surface-card rounded-xl p-4" data-testid="review-view">
        <h2 className="text-lg font-semibold text-text mb-2">Your Review</h2>
        <StarRating value={rating} />
        {comment && <p className="mt-2 text-sm text-text">{comment}</p>}
      </div>
    );
  }

  return (
    <div
      className={`${lead ? 'mt-4' : 'mt-8'} bg-surface-card rounded-xl p-4${highlight ? ' ring-2 ring-primary' : ''}`}
      data-testid="review-form"
    >
      <h2 className="text-lg font-semibold text-text mb-2">
        {existing ? 'Edit your review' : 'Rate your trip'}
      </h2>
      <StarRating value={rating} onChange={setRating} size={lead ? 44 : 24} />
      <textarea
        data-testid="review-comment"
        value={comment}
        onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)}
        placeholder="Anything other students should know? (optional)"
        maxLength={2000}
        className="mt-3 w-full rounded-lg bg-surface-mid text-text p-3 text-sm"
        rows={3}
      />
      {err && <p className="mt-2 text-sm text-red-400">{err}</p>}
      <Button className="mt-3 w-full" data-testid="review-submit" loading={saving} onClick={submit}>
        {existing ? 'Save Changes' : 'Send review'}
      </Button>
    </div>
  );
}

/**
 * 325 (FR-003): a paid extension is stored as `checked_out` the moment it is paid (304: no second
 * handover), before it starts. Until its time comes it reads as what it is to the customer: booked.
 */
function legStatus(leg: { status: string; pickup_at: string; is_extension: boolean }) {
  return leg.is_extension && leg.status === 'checked_out' && Date.parse(leg.pickup_at) > Date.now()
    ? 'confirmed'
    : leg.status;
}

/** 325: a leg's pickup or return, short (date and time, no seconds); 346: in Malaysia time. */
function legTime(iso: string) {
  return mytDateTime(iso, 'short');
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-400">{label}</span>
      <span className="text-text">{value}</span>
    </div>
  );
}
